// Low-level, framework-free date/ISO helpers shared by the Boxes and the converters.
// All parsing/formatting works in **local** time (the calendar is a wall-clock picker).

export function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Local `Date` → `"YYYY-MM-DD"`. */
export function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Local `Date` → `"YYYY-MM-DDTHH:mm"`. */
export function formatIsoDateTime(date: Date): string {
  return `${formatIsoDate(date)}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** `"YYYY-MM-DD"` → local `Date` at midnight, or `null` when malformed / out of calendar range. */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // Reject rollovers such as 2025-02-30 → 2025-03-02.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** `"YYYY-MM-DD"` or `"YYYY-MM-DDTHH:mm(:ss)"` → local `Date` (time defaults to 00:00). */
export function parseIsoDateTime(value: string): Date | null {
  const [datePart, timePart] = value.trim().split('T');
  const base = parseIsoDate(datePart ?? '');
  if (!base) return null;
  if (timePart) {
    const time = /^(\d{1,2}):(\d{1,2})/.exec(timePart);
    if (time) {
      base.setHours(Math.min(23, Number(time[1])), Math.min(59, Number(time[2])), 0, 0);
    }
  }
  return base;
}
